import { observer } from 'mobx-react-lite'
import { __cpl } from './__cpl'
import { Button, Popconfirm, Segmented, Space, Tooltip } from "antd";
import i from '../../../translations/i'
import {
    CheckOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    LockOutlined,
    PlusOutlined,
    ShoppingOutlined,
} from '@ant-design/icons'
import React from "react";
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { canManageOwnedRecord } from "../../../helpers/manager_ownership";
import { moment_custom } from '../../../helpers/timestamp/datetime.helper'
import { CardList, cleanText, ListCard, ListChip } from '../../../components/card_list/CardList'

dayjs.extend(utc)

const formatDate = (value: string | null) => value ? moment_custom(dayjs.utc(value).valueOf(), 'D MMM YYYY') : null

// Описание приходит из редактора — в карточке нужен только текст
const plainText = (html: any) => cleanText(html).replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()

const ListCardItem = observer(({ list, type, multiple, setFunction }: { list: any, type: string, multiple: boolean, setFunction: any }) => {
    const editable = canManageOwnedRecord(list)
    const selected = type === 'group' && __cpl.selectedLists.includes(list.id)
    const description = plainText(list.description)
    const crm = cleanText(list.id_crm)
    const slug = cleanText(list.name_encode)
    const updated = list.updated_at && list.updated_at !== list.created_at ? formatDate(list.updated_at) : null

    const remove = (
        <Popconfirm
            placement="left"
            title={i.t('def:confirms.delete')}
            onConfirm={() => __cpl.deleteOne(list.id)}
            okText={i.t('def:yes')}
            cancelText={i.t('def:no')}
            disabled={!editable}
        >
            <Tooltip title={i.t('def:actions.remove')}>
                <Button danger shape="circle" icon={<DeleteOutlined />} disabled={!editable} />
            </Tooltip>
        </Popconfirm>
    )

    return (
        <ListCard
            href={editable && type === 'list' ? `/catalog/lists/${list.id}` : undefined}
            onOpen={editable && type === 'group' ? () => __cpl.setEditedList(list.id) : undefined}
            selected={selected}
            title={cleanText(list.name) || `#${list.id}`}
            titleHint={list.name}
            titleExtra={<>
                {list.is_system && <ListChip tone="info">{i.t('productLists:system')}</ListChip>}
                {!editable &&
                    <ListChip icon={<LockOutlined />} tooltip={i.t('productLists:readonlyHint')}>{i.t('productLists:readonly')}</ListChip>
                }
            </>}
            subtitle={<>ID {list.id}{slug && <> · /{slug}</>}</>}
            text={description || undefined}
            chips={<>
                {crm && <ListChip copy={crm} tooltip={i.t('def:id_crm')}>CRM {crm}</ListChip>}
                {updated
                    ? <ListChip icon={<ClockCircleOutlined />}>{i.t('productLists:updated', { date: updated })}</ListChip>
                    : list.created_at && <ListChip icon={<ClockCircleOutlined />}>{i.t('productLists:created', { date: formatDate(list.created_at) })}</ListChip>
                }
            </>}
            actions={<>
                {type === 'group' &&
                    <Button
                        type={selected ? 'primary' : 'default'}
                        icon={selected ? <CheckOutlined /> : undefined}
                        onClick={() => {
                            if(setFunction) setFunction(list.id)
                            __cpl.selectList(list.id, multiple)
                        }}
                    >
                        {selected ? i.t('def:selected') : i.t('def:select')}
                    </Button>
                }
                <Tooltip title={i.t('def:actions.edit')}>
                    <Button
                        shape="circle"
                        icon={<EditOutlined />}
                        disabled={!editable}
                        {...(type === 'list'
                            ? { href: `/catalog/lists/${list.id}` }
                            : { onClick: () => __cpl.setEditedList(list.id) }
                        )}
                    />
                </Tooltip>
                {remove}
            </>}
        />
    )
})

export const CustomProductListsList = observer(({type = 'list', selectedElements = [], multiple = true, setFunction = null}: any) => {
    selectedElements = selectedElements.filter((el: any) => el && el?.length !== 0)

    const renderItem = (list: any) => <ListCardItem list={list} type={type} multiple={multiple} setFunction={setFunction} />

    return <>
        {type === 'group' && <>
            <Space>
                <Button>{i.t('def:selected')}: {__cpl.selectedLists.length}</Button>
                <Button danger onClick={() => __cpl.selectedLists = []}>{i.t('def:reset')}</Button>
            </Space>
            <br /><br />
            {selectedElements.length > 0 && <>
                <CardList
                    title={i.t('def:Selected Lists')}
                    items={selectedElements}
                    renderItem={renderItem}
                />
                <br />
            </>}
        </>}

        <CardList
            items={__cpl.list}
            loading={__cpl.loading}
            // Бэкенд может отдать все списки сразу без total
            count={__cpl.pagination.total || __cpl.list.length}
            extra={
                <Segmented
                    value={__cpl.is_system}
                    options={[
                        { label: i.t('def:is_system_false'), value: false },
                        { label: i.t('def:is_system_true'), value: true },
                    ]}
                    onChange={(e) => {
                        __cpl.is_system = e as boolean
                        __cpl.pagination = {
                            limit: 10,
                            page: 1,
                            total: 0
                        }
                        __cpl.list = []
                        __cpl.getList()
                    }}
                />
            }
            renderItem={renderItem}
            empty={__cpl.is_system
                ? { icon: <ShoppingOutlined />, title: i.t('productLists:emptySystem') }
                : {
                    icon: <ShoppingOutlined />,
                    title: i.t('productLists:empty'),
                    text: i.t('productLists:emptyHint'),
                    action: type === 'list'
                        ? <Button type="primary" icon={<PlusOutlined />} href="/catalog/lists/0">{i.t('def:Add List')}</Button>
                        : <Button type="primary" icon={<PlusOutlined />} onClick={() => __cpl.setEditedList(0)}>{i.t('def:Add List')}</Button>,
                }
            }
            pagination={{
                current: __cpl.pagination.page,
                pageSize: __cpl.pagination.limit,
                total: __cpl.pagination.total,
                onChange: (page: number) => __cpl.setPage(page),
            }}
        />
    </>
})
